/**
 * `SetupGuideCard` — ADR-0167 §3, redesigned per ADR-0200 (2026-08-05).
 *
 * Five ACTIONABLE steps, each auto-derived from server truth the client
 * can already read (no new endpoints, no manual check-off):
 *
 *   1. Install and verify      — `status` is `discovery_active` /
 *                                `transactional_active` (unchanged).
 *   2. Set your rules          — the active policy is merchant-authored:
 *                                `version !== 'v1-default'` (the seed
 *                                label exists precisely to make the
 *                                auto-seeded baseline distinguishable —
 *                                services/api default-policy.ts).
 *   3. Connect your store      — Shopify OR WooCommerce connector
 *                                `connected`.
 *   4. Enable governed checkout— a settle rail exists: Stripe connected
 *                                OR Shopify connected (Shopify checkout
 *                                settles via Shopify).
 *   5. Get escalation alerts   — Slack webhook configured + enabled
 *                                (ADR-0098; email alerts are the
 *                                default — Slack is the upgrade).
 *
 * The prior v1 steps 2-3 ("First traffic recorded" / "First agent
 * identified") were OBSERVATIONS the merchant could not act on — founder
 * verdict 2026-08-05: useless as to-dos. They remain visible on the site
 * cards / activity feed / dashboard where they belong.
 *
 * Detection reads fire in ONE parallel batch, only when the card is
 * visible, not dismissed, and step 1 is done (before verify nothing
 * else can be true). Unknown (unfetched / failed) renders as not-done —
 * never guessed (ADR-0167 posture, unchanged). Targets the account's
 * most-progressed site; hides when all steps complete, when there are
 * no sites, or after dismissal (browser-local). Deliberately no
 * per-step "skip" — that would reintroduce manual check-off state; the
 * card-level dismiss is the pressure valve for non-commerce sites that
 * will never connect a cart.
 *
 * `deriveSetupSteps` / `mostProgressedSite` are pure and exported for
 * unit tests.
 */

import { ArrowRight, Check, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { Site } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { useSites } from '../../app/providers.js';
import type { View } from '../../app/view-router.js';
import {
  dismissSetupGuide,
  isSetupGuideDismissed,
} from '../../lib/onboarding-preferences.js';
import { useC } from '../../lib/theme.js';
import { Card } from '../../components/atoms/index.js';
import { WAFBlockBanner } from '../../components/WAFBlockBanner.js';
import { useCrawlerAccess } from '../../hooks/useCrawlerAccess.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/** The seeded baseline policy's version label (services/api
 *  default-policy.ts `DEFAULT_POLICY_VERSION`). A merchant-authored
 *  publish always carries a different label. */
export const SEEDED_POLICY_VERSION = 'v1-default';

export type SetupStepId =
  | 'install_verify'
  | 'guardrails'
  | 'connect_store'
  | 'catalog_rules'
  | 'payment_rail'
  | 'escalation_alerts';

export interface SetupStep {
  readonly id: SetupStepId;
  readonly label: string;
  readonly done: boolean;
}

/**
 * Detection reads for steps 2-6 — `null` = unknown (not yet fetched /
 * fetch failed), which renders as not-done rather than guessing.
 */
export interface SetupDetections {
  /** Active policy version label, or null while unknown. */
  readonly policy_version: string | null;
  readonly shopify_connected: boolean | null;
  readonly woocommerce_connected: boolean | null;
  readonly stripe_connected: boolean | null;
  readonly slack_configured: boolean | null;
  /**
   * True when the active policy has ≥1 enabled catalog rule scoped to
   * `search` or `both` — the prerequisite for browse_catalog to appear
   * in tools/list. Derived from the same policy fetch as policy_version
   * (no extra API call). Null while unknown.
   */
  readonly catalog_rules_active: boolean | null;
}

export const UNKNOWN_DETECTIONS: SetupDetections = {
  policy_version: null,
  shopify_connected: null,
  woocommerce_connected: null,
  stripe_connected: null,
  slack_configured: null,
  catalog_rules_active: null,
};

export function siteVerified(site: Site): boolean {
  return (
    site.status === 'discovery_active'
    || site.status === 'transactional_active'
  );
}

function sitePinged(site: Site): boolean {
  return site.last_seen_at !== undefined && site.last_seen_at !== null;
}

/**
 * ADR-0200 amendment — whether the commerce steps (connect store /
 * payment rail) belong in this site's guide. FAIL-SAFE, three rules:
 *
 *   1. An existing commerce connection ALWAYS keeps the steps — a
 *      WordPress site with a Stripe rail must not lose its checkmark.
 *   2. Hide only on a POSITIVELY non-store platform hint — `wordpress`
 *      ONLY. NOT `cloudflare`: the detector emits `'cloudflare'` as a
 *      fallback when a CF header is seen and NO platform signal is
 *      recognised (platform-detect.ts `classifyCore`) — absence of
 *      evidence, not evidence of absence. Magento / BigCommerce /
 *      headless stores behind Cloudflare classify as `cloudflare`, and
 *      ADR-0197's skill-recommendation precedent fails OPEN on the
 *      same signal. `other_known` stays too — it covers commerce
 *      platforms Sill has no connector for (Magento, BigCommerce, …)
 *      where the guidance's webhook-skills escape hatch is exactly
 *      right.
 *   3. Absent / `unknown` / `cloudflare` hint keeps the steps (mirror
 *      of the "unknown detection renders as not-done, never guessed"
 *      posture: unknown platform ⇒ never hide).
 */
export function commerceCapable(site: Site, d: SetupDetections): boolean {
  if (
    d.shopify_connected === true
    || d.woocommerce_connected === true
    || d.stripe_connected === true
  ) {
    return true;
  }
  return site.detected_platform !== 'wordpress';
}

/**
 * Derive the actionable steps for a site (ADR-0200; amendment: the two
 * commerce steps are filtered out for positively non-store platforms,
 * so a plain-WordPress site can actually COMPLETE its guide at 3-of-3
 * instead of pinning forever on "Connect your store").
 */
export function deriveSetupSteps(
  site: Site,
  d: SetupDetections,
): readonly SetupStep[] {
  const verified = siteVerified(site);
  const guardrails_done =
    d.policy_version !== null && d.policy_version !== SEEDED_POLICY_VERSION;
  const store_done =
    d.shopify_connected === true || d.woocommerce_connected === true;
  // Shopify checkout IS a settle rail — a Shopify-connected site does
  // not additionally need Stripe for governed checkout.
  const rail_done = d.stripe_connected === true || d.shopify_connected === true;
  const commerce = commerceCapable(site, d);
  const steps: SetupStep[] = [
    { id: 'install_verify', label: 'Install and verify', done: verified },
    { id: 'guardrails', label: 'Set your rules', done: guardrails_done },
  ];
  if (commerce) {
    steps.push(
      { id: 'connect_store', label: 'Connect your store', done: store_done },
      {
        id: 'catalog_rules',
        label: 'Activate catalog rules',
        done: d.catalog_rules_active === true,
      },
      { id: 'payment_rail', label: 'Enable governed checkout', done: rail_done },
    );
  }
  steps.push({
    id: 'escalation_alerts',
    label: 'Get escalation alerts',
    done: d.slack_configured === true,
  });
  return steps;
}

/**
 * Pick the site the guide should track: the non-deleted site furthest
 * along (verified + pinged > verified > pinged > neither; ties go to
 * the earlier row, i.e. the site the merchant set up first). Null when
 * the list is empty. (Unchanged from v1 — `last_seen_at` still breaks
 * ties even though "first traffic" is no longer a step.)
 */
export function mostProgressedSite(
  sites: ReadonlyArray<Site>,
): Site | null {
  let best: Site | null = null;
  let bestScore = -1;
  for (const s of sites) {
    if (s.deleted_at !== undefined && s.deleted_at !== null) continue;
    const score = (siteVerified(s) ? 2 : 0) + (sitePinged(s) ? 1 : 0);
    if (score > bestScore) {
      best = s;
      bestScore = score;
    }
  }
  return best;
}

/** Per-step guidance for the first not-done step. */
const STEP_GUIDANCE: Record<
  SetupStepId,
  { body: string; cta: string; view: View }
> = {
  install_verify: {
    body: 'Install the snippet and verify you own the domain. Not the person who edits your site? Invite whoever is from the install step.',
    cta: 'Continue setup',
    view: 'onboarding',
  },
  guardrails: {
    body: 'Every agent action is governed by your policy. You start on a conservative baseline — review the caps and rules, then publish your own.',
    cta: 'Review guardrails',
    view: 'guardrails',
  },
  connect_store: {
    body: 'Connect Shopify or WooCommerce so agents can read your catalog and check orders. Running something else? Custom skills connect any backend via webhooks in Settings → Skills.',
    cta: 'Connect store',
    view: 'settings',
  },
  catalog_rules: {
    body: 'Catalog rules control which products agents can discover. Accept the suggested rule to make your in-stock products searchable — this is what makes browse_catalog and check_availability work.',
    cta: 'Activate rules',
    view: 'catalog',
  },
  payment_rail: {
    body: 'Connect Stripe so agents can place orders under your guardrails — every charge is policy-checked and signed into the audit log. On Shopify, checkout settles through Shopify instead (test mode only for now).',
    cta: 'Connect payments',
    view: 'settings',
  },
  escalation_alerts: {
    body: 'Escalated mandates email your team by default. Add Slack to get them in-channel with a one-click review link.',
    cta: 'Add Slack',
    view: 'settings',
  },
};

export interface SetupGuideCardProps {
  setView: (v: View) => void;
}

export function SetupGuideCard({
  setView,
}: SetupGuideCardProps): JSX.Element | null {
  const C = useC();
  const api = useApi();
  const { activeSite, setActiveSite } = useSites();
  const [dismissed, setDismissed] = useState<boolean>(() =>
    isSetupGuideDismissed(),
  );
  const [detections, setDetections] =
    useState<SetupDetections>(UNKNOWN_DETECTIONS);
  // ADR-0211 §5.4 — one line under a DONE step 1 when the coverage read
  // says a layer is still uncovered. Text only; the action itself lives
  // on the InstallCoverageCard in Settings → Integrations. `null` = not
  // read / read failed / nothing uncovered → no line.
  const [coverage_note, setCoverageNote] = useState<string | null>(null);

  const site = activeSite;
  // The detection reads are only worth firing once step 1 is done —
  // before verify none of the later steps can be true.
  const wantDetections = site !== null && siteVerified(site);

  // ADR-0207 Slice 2 — surface the WAF-block warning when the readiness
  // pipeline reports the merchant's firewall is 403-ing the anonymous
  // scanner. Uses the SAME public /v1/domain-readiness endpoint the
  // marketing scan uses (dashboard origin is on its CORS allow-list).
  // `null` on any failure — the banner is additive.
  const crawler = useCrawlerAccess(site?.domain);

  useEffect(() => {
    if (dismissed || !wantDetections || site === null) return;
    let cancelled = false;
    // Each read fails independently to `null` (renders as not-done —
    // never guessed). The thunk wrapper also swallows SYNCHRONOUS
    // throws (e.g. a partial API fake in tests missing a method).
    const safe = async <T,>(fn: () => Promise<T>): Promise<T | null> => {
      try {
        return await fn();
      } catch {
        return null;
      }
    };
    void (async (): Promise<void> => {
      // ONE parallel batch. The three commerce reads fire even on a
      // non-store platform hint — deliberately: `commerceCapable`'s
      // connection-override (rule 1) needs them, or a WordPress site
      // with a live Stripe rail would have its completed steps hidden.
      const [policy, shopify, woocommerce, stripe, slack, coverage] = await Promise.all([
        safe(() => api.policy.loadState(site.site_id)),
        safe(() => api.sites.getConnectorStatus(site.site_id, 'shopify')),
        safe(() => api.sites.getConnectorStatus(site.site_id, 'woocommerce')),
        safe(() => api.sites.getConnectorStatus(site.site_id, 'stripe')),
        safe(() => api.sites.getSlackWebhookStatus(site.site_id)),
        safe(() => api.sites.getInstallCoverage(site.site_id)),
      ]);
      if (cancelled) return;
      if (coverage !== null && coverage.action !== null
        && coverage.action.kind !== 'connect_store') {
        // 2026-10-28 — the discovery layer is only `live` when the
        // card is actually served (or redirected to the Sill edge).
        // 'slow' or 'unknown' is NOT live, but it is not actionable
        // either, so it maps to 'waiting' here, same as 'missing'.
        const card_served =
          coverage.discovery.agent_card === 'served'
          || coverage.discovery.agent_card === 'redirect';
        const layers = [coverage.browser.state, coverage.server_side.state, card_served ? 'live' : 'waiting'];
        const measurable = layers.filter((l) => l !== 'unavailable');
        const live = measurable.filter((l) => l === 'live').length;
        setCoverageNote(`Coverage: ${live} of ${measurable.length} layers live — see Settings → Integrations.`);
      } else {
        setCoverageNote(null);
      }
      setDetections({
        policy_version: policy?.active?.version ?? null,
        shopify_connected:
          shopify === null ? null : shopify.status === 'connected',
        woocommerce_connected:
          woocommerce === null ? null : woocommerce.status === 'connected',
        stripe_connected:
          stripe === null ? null : stripe.status === 'connected',
        slack_configured:
          slack === null ? null : slack.configured && slack.enabled,
        // Derived from the same policy fetch — no extra API call.
        // True when the active policy has ≥1 enabled catalog rule scoped
        // to 'search' or 'both' (the browse_catalog prerequisite).
        catalog_rules_active:
          policy === null
            ? null
            : (policy.active?.rules ?? []).some(
                (r) =>
                  r.category === 'catalog'
                  && r.enabled === true
                  && (
                    (r.params as { applies_to?: string }).applies_to === 'search'
                    || (r.params as { applies_to?: string }).applies_to === 'both'
                  ),
              ),
      });
    })();
    return (): void => {
      cancelled = true;
    };
    // `site.site_id` (not the object) — the list refresh mints new row
    // objects for the same site and must not re-fire the fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, dismissed, wantDetections, site?.site_id]);

  if (dismissed || site === null) return null;

  // While verification-dependent detections are in flight, don't flash
  // "1 of N" — return null until the parallel batch resolves.
  if (wantDetections && detections === UNKNOWN_DETECTIONS) return null;

  const steps = deriveSetupSteps(site, detections);
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;

  const current = steps.find((s) => !s.done);
  const guidance = current !== undefined ? STEP_GUIDANCE[current.id] : null;

  return (
    <Card className="p-5 mb-8" data-testid="setup-guide-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.limeFg,
              letterSpacing: 2,
            }}
          >
            SETUP GUIDE
          </span>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1,
            }}
            data-testid="setup-guide-progress"
          >
            {doneCount} OF {steps.length} · {site.domain}
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            dismissSetupGuide();
            setDismissed(true);
          }}
          aria-label="Hide setup guide"
          className="flex items-center gap-1"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
          }}
          data-testid="setup-guide-dismiss"
        >
          <X size={12} /> Hide
        </button>
      </div>

      {/* ADR-0207 Slice 2 — WAF-block warning is a TOP-PRIORITY banner
          above the checklist. When the merchant's firewall is 403-ing
          the anonymous scanner every step-2..N signal is unverifiable
          from outside, so surface the fix (three paths to exempt)
          before the setup steps themselves. */}
      {crawler.status === 'ready' && crawler.access === 'blocked' ? (
        <WAFBlockBanner />
      ) : null}

      <ul className="space-y-2" style={{ listStyle: 'none', padding: 0 }}>
        {steps.map((s) => {
          const isCurrent = current !== undefined && s.id === current.id;
          const destination = STEP_GUIDANCE[s.id].view;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => {
                  setActiveSite(site);
                  setView(destination);
                }}
                className="flex items-center gap-2.5 w-full text-left rounded-sm transition-opacity hover:opacity-70 active:opacity-50"
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '2px 0',
                  cursor: 'pointer',
                }}
                aria-label={`${s.label}${s.done ? ' (done)' : ''} — go to ${destination}`}
              >
                <span
                  className="flex items-center justify-center"
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 2,
                    background: s.done ? C.limeDim : 'transparent',
                    border: `1px solid ${s.done ? C.limeFg : C.border}`,
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                >
                  {s.done ? <Check size={10} color={C.limeFg} /> : null}
                </span>
                <span
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: s.done ? C.muted : C.text,
                    fontWeight: isCurrent ? 600 : 400,
                    flex: 1,
                  }}
                >
                  {s.label}
                </span>
                {!s.done && !isCurrent ? (
                  <ArrowRight
                    size={11}
                    style={{ color: C.muted, flexShrink: 0 }}
                    aria-hidden="true"
                  />
                ) : null}
              </button>

              {/* ADR-0211 §5.4 — coverage note under a DONE install step. */}
              {s.id === 'install_verify' && s.done && coverage_note !== null ? (
                <div
                  className="ml-2 pl-4 mt-1"
                  style={{ borderLeft: `2px solid ${C.borderSoft}`, fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.amberFg }}
                  data-testid="setup-guide-coverage-note"
                >
                  {coverage_note.toUpperCase()}
                </div>
              ) : null}

              {/* Guidance nests under ITS OWN step (indented, left
                  rule) — the placement fix from the 2026-07-09 founder
                  UX review, unchanged. */}
              {isCurrent && guidance !== null ? (
                <div
                  className="flex flex-col sm:flex-row sm:items-center gap-3 mt-2 ml-2 pl-4 py-1"
                  style={{ borderLeft: `2px solid ${C.borderSoft}` }}
                >
                  <p
                    style={{
                      fontFamily: sansFont,
                      fontSize: 12,
                      color: C.muted,
                      lineHeight: 1.55,
                      margin: 0,
                      flex: 1,
                    }}
                  >
                    {guidance.body}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveSite(site);
                      setView(guidance.view);
                    }}
                    className="flex items-center justify-center gap-2 px-4 py-2 self-start sm:self-auto"
                    style={{
                      background: C.lime,
                      color: C.ink,
                      fontFamily: sansFont,
                      fontWeight: 600,
                      fontSize: 12,
                      borderRadius: 2,
                      whiteSpace: 'nowrap',
                    }}
                    data-testid="setup-guide-cta"
                  >
                    {guidance.cta} <ArrowRight size={13} />
                  </button>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
